import { AzureFileEntityTypeEnum, AzureFileTypeEnum, StorageOwnerTypeEnum } from '../../Enums/AzureFileTypeEnum';
import { StorageFileViewModel } from './StorageBrowser';
import { ErrorType } from '../../HttpError';

/** Body of POST contracts/files/sas-token */
export interface UploadSasTokenRequest {
  fileType: AzureFileTypeEnum;
  entityType: AzureFileEntityTypeEnum;
  entityId: number;
  /** Non-integer entity id (service GUID, contract §10.1); used in the blob path instead of entityId */
  entityKey?: string;
  /** Original name without extension + "_" + yyyyMMddTHHmmss */
  fileName: string;
  /** Lower case, without dot */
  fileExtension: string;
  /** Storage folder owner (contract §2, "Request de SAS ampliado") */
  ownerType?: StorageOwnerTypeEnum;
  /** Owner id as string (int ids or practitioner GUID) */
  ownerId?: string;
  /** Only when ownerType is Sponsor */
  protocolId?: number;
}

/** Storage folder owner of the uploaded files */
export interface AzureFileOwner {
  ownerType: StorageOwnerTypeEnum;
  ownerId: string;
  protocolId?: number;
}

/** ContractFileSasTokenViewModel */
export interface UploadSasToken {
  sasUrl: string;
  blobPath: string;
  expiresAtUtc: string;
  method: string;
}

/** Entity that owns the uploaded files */
export interface AzureFileTarget {
  entityType: AzureFileEntityTypeEnum;
  entityId: number;
  /** Non-integer entity id (service GUID, contract §10.1). When present, entityId is 0. */
  entityKey?: string;
  /** Storage owner sent with the SAS request (blob path: tenant/owner/[protocol]/...) */
  owner?: AzureFileOwner;
}

/** File type that can be chosen in the upload drawer */
export interface AllowedAzureFileType {
  fileType: AzureFileTypeEnum;
  /** i18n key of the visible label */
  labelKey: string;
  /** Allowed extensions, lower case, without dot */
  extensions: string[];
}

/** Which service owns the registered files list for a target */
export type AzureFileListLoaderKind =
  | 'provider'
  | 'protocolSponsor'
  | 'settlement'
  | 'service'
  /** CashFlow payment receipts (contract §12.2): payments/{id}/receipts */
  | 'paymentReceipt'
  /** CashFlow creditor invoices (contract §12.2): creditors/{guid}/invoices */
  | 'creditorInvoice';

/** Owners whose drawer can link / unlink existing files (provider files since spec 005: providers/{id}/files/link) */
export type AzureFileLinkKind = 'protocolSponsor' | 'settlement' | 'service' | 'paymentReceipt' | 'provider';

/** MedicalProtocols ProviderFileTypeEnum (provider files list / link `fileType`). */
export enum ProviderFileTypeEnum {
  Invoice = 1,
  ServicesPerformed = 2,
}

/** Request handled by the upload thunk */
export interface AzureFileUploadRequest {
  file: File;
  fileType: AzureFileTypeEnum;
  target: AzureFileTarget;
  /** Extra fields sent with the register call (creditor invoices: number, date, amount, source...) */
  registerExtra?: Record<string, unknown>;
}

export interface AzureFileUploadResult {
  blobPath: string;
  fileName: string;
  fileExtension: string;
  /** Path relative to the tenant (sent to the register endpoint) */
  relativePath?: string;
  /** The owner service registered the file right after the upload (contract §9.5) */
  registered?: boolean;
  /** Why the register call failed (the blob is uploaded anyway) */
  registerError?: ErrorType;
}

/** Origin of a registered file (contract §9.3): uploaded to the entity folder or linked from another folder. */
export enum AzureFileOriginEnum {
  Uploaded = 1,
  Linked = 2,
}

/** Body of POST .../files/register and POST settlements/{id}/files/link */
export interface AzureFilePathRequest {
  /** Path relative to the tenant root */
  path: string;
}

/** Body of POST protocols/{id}/sponsor-files/link */
export interface SponsorFileLinkRequest extends AzureFilePathRequest {
  sponsorId: number;
}

/**
 * Body of POST protocols/providers/{ProviderId:int}/files/link (spec 005): mirror of the sponsor-files link
 * plus the optional MedicalProtocols file type (1 Invoice, 2 ServicesPerformed). 409 = already linked.
 */
export interface ProviderFileLinkRequest extends AzureFilePathRequest {
  fileType?: ProviderFileTypeEnum;
}

/** Body of POST services/{id}/files/link (sponsorId optional, contract §10.3) */
export interface ServiceFileLinkRequest extends AzureFilePathRequest {
  sponsorId?: number;
}

/** Registered file view model of any owner service */
export type OwnerFileViewModel =
  | ProviderFileViewModel
  | SponsorFileViewModel
  | SettlementFileViewModel
  | ServiceFileViewModel
  | PaymentReceiptViewModel
  | CreditorInvoiceFileViewModel;

/** CashFlow PaymentReceipts (contract §12.2) */
export interface PaymentReceiptViewModel {
  id: number;
  paymentId: number;
  /** With tenant */
  blobPath: string;
  /** Relative to the tenant (used for the read SAS) */
  path?: string | null;
  fileName: string;
  fileExtension: string;
  fileSize?: number | null;
  /** 1 Uploaded, 2 Linked */
  origin?: number;
  created?: string | null;
  uploadedAt?: string | null;
}

/** Minimal shape of a CashFlow CreditorInvoice seen as a registered file */
export interface CreditorInvoiceFileViewModel {
  id: number;
  blobPath: string;
  path?: string | null;
  fileName: string;
  fileExtension: string;
  fileSize?: number | null;
  created?: string | null;
  invoiceDate?: string | null;
}

// ---- Registered files (owner services view models) ----

export interface ProviderFileViewModel {
  id: string;
  providerId: number;
  /** 1 Invoice, 2 ServicesPerformed */
  fileType: number;
  fileName: string;
  fileExtension: string;
  fileSize?: number | null;
  blobPath: string;
  status: number;
  uploadedAt: string;
  /** 1 Uploaded, 2 Linked (sponsor and settlement files) */
  origin?: number;
}

export interface SponsorFileViewModel {
  id: string;
  protocolId: number;
  sponsorId?: number | null;
  /** 1 PaymentProposal */
  fileType: number;
  fileName: string;
  fileExtension: string;
  fileSize?: number | null;
  blobPath: string;
  status: number;
  uploadedAt: string;
  /** 1 Uploaded, 2 Linked (sponsor and settlement files) */
  origin?: number;
}

export interface SettlementFileViewModel {
  id: number;
  settlementId: number;
  /** 1 Invoice */
  fileType: number;
  ownerType: number;
  ownerId: string;
  fileName: string;
  fileExtension: string;
  fileSize?: number | null;
  blobPath: string;
  status: number;
  uploadedAt: string;
  /** 1 Uploaded, 2 Linked (sponsor and settlement files) */
  origin?: number;
}

/** MedicalProtocols ServiceFiles (contract §10.3) */
export interface ServiceFileViewModel {
  id: string;
  serviceId: string;
  protocolId: number;
  sponsorId?: number | null;
  /** 1 Receipt */
  fileType: number;
  /** 1 Uploaded, 2 Linked */
  origin?: number;
  fileName: string;
  fileExtension: string;
  fileSize?: number | null;
  blobPath: string;
  status: number;
  uploadedAt: string;
}

/** Normalized registered file shown in the drawer */
export interface RegisteredAzureFile {
  id: string;
  fileName: string;
  fileExtension: string;
  fileSize?: number | null;
  uploadedAt: string;
  /** Blob path registered by the owner service (tenant/.../file.ext) */
  blobPath?: string;
  /** i18n key describing the file type */
  typeLabelKey: string;
  /** Uploaded (default) or Linked */
  origin: AzureFileOriginEnum;
  /** Path relative to the tenant when the owner service sends it (CashFlow) */
  path?: string;
}

// ---- Files of a target read from the storage (source of truth) ----

/** Storage folder of a target for one file type (relative to the tenant root) */
export interface TargetStorageFolder {
  fileType: AzureFileTypeEnum;
  path: string;
}

/** A blob found in one of the target folders */
export interface TargetStorageFile {
  fileType: AzureFileTypeEnum;
  file: StorageFileViewModel;
}

/** Registration of a storage file by the owner service (arrives by Service Bus event) */
export type StorageFileRegistrationStatus = 'registered' | 'pending' | 'unknown' | 'linked';

/** Row of the upload drawer list */
export interface DrawerStorageFile {
  key: string;
  /** Path relative to the tenant root (used for the read SAS) */
  path: string;
  name: string;
  extension: string;
  size: number;
  lastModified: string;
  fileType: AzureFileTypeEnum;
  /** i18n key of the type */
  typeLabelKey: string;
  status: StorageFileRegistrationStatus;
  /** Linked rows: id of the link record (used to unlink) */
  registeredId?: string;
  /** Linked rows: folder where the file really lives (relative to the tenant) */
  location?: string;
}
